import { AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { FormControl, UntypedFormArray, UntypedFormControl, UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import { AppState } from '../../../store';
import { Store } from '@ngrx/store';
import { combineLatest, merge, Observable, Subject, Subscription } from 'rxjs';
import { selectRouteParam } from '../../../store/router/router.state';
import { Shipment } from '../../../models/shipment';
import { ShipmentService } from '../../../data/shipment.service';
import { delay, distinctUntilChanged, filter, map, startWith, take, tap, withLatestFrom } from 'rxjs/operators';
import { getDocumentTypeLabel } from '../../../list/document-types';
import {
    selectQuickQuote, selectShipmentEditorCategory,
    selectShipmentEditorDeclaration, selectShipmentEditorDestination, selectShipmentEditorHasInsurance,
    selectShipmentEditorInsuranceCategory,
    selectShipmentEditorInsuranceProvisionId, selectShipmentEditorInternational, selectShipmentEditorLoading, selectShipmentEditorPackages,
    selectShipmentEditorShipment, selectShipmentEditorShipper, selectShipmentEditorValid, selectShipmentEditorViolations
} from '../../../store/shipment-editor/shipment-editor.state';
import {
    selectCourierForms,
    selectCourierOptions,
    selectInsuranceCategories,
    selectReferenceDefinitions
} from '../../../store/profile/profile.state';
import { CardTypeEnum } from '../../../enum/card-type.enum';
import { Violation } from '../../../models/violation';
import { ShipmentValidatorService } from '../../shared/services/shipment-validator.service';
import { ShipmentOptionsComponent } from '../shipment-options/shipment-options.component';
import { ShipmentInternationalComponent } from '../shipment-international/shipment-international.component';
import { ShipmentPackagesComponent } from '../shipment-packages/shipment-packages.component';
import { CardFormComponent } from '../../shared/card-form/card-form.component';
import { QuickCardFormComponent } from '../../shared/quick-card-form/quick-card-form.component';
import { AsyncPipe } from '@angular/common';
import { SignatureOptionEnum } from '../../../enum/signature-option.enum';
import { MatButtonToggle, MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatAccordion, MatExpansionModule } from '@angular/material/expansion';
import { ShipmentSummary } from '../../../utils/shipment-summary';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ShipmentReferencesComponent } from '../shipment-references/shipment-references.component';
import { MatCard, MatCardContent, MatCardHeader, MatCardTitle } from '@angular/material/card';
import { MatButton } from '@angular/material/button';
import { shipmentEditorGetQuotes, shipmentEditorOpenDocumentUploadModal } from '../../../store/shipment-editor/shipment-editor.actions';
import { ShipmentCategoryEnum } from '../../../enum/shipment-category.enum';
import { ShipmentInsuranceComponent } from '../shipment-insurance/shipment-insurance.component';
import { ShipmentDocumentsComponent } from '../shipment-documents/shipment-documents.component';
import { selectShipmentEditorDocuments } from '../../../store/shipment-editor/shipment-editor.state';

@Component({
    selector: 'app-shipment-editor',
    templateUrl: './shipment-editor.component.html',
    styleUrls: ['./shipment-editor.component.scss'],
    imports: [ ReactiveFormsModule, QuickCardFormComponent, CardFormComponent, ShipmentPackagesComponent, ShipmentInternationalComponent, ShipmentOptionsComponent, AsyncPipe, MatButtonToggleGroup, MatButtonToggle, MatAccordion, MatExpansionModule, ShipmentReferencesComponent, MatCard, MatCardTitle, MatCardHeader, MatCardContent, MatButton, ShipmentInsuranceComponent, ShipmentDocumentsComponent ]
})
export class ShipmentEditorComponent {

    constructor(private store: Store<AppState>, private shipmentValidator: ShipmentValidatorService,
                private breakpointObserver: BreakpointObserver) {}

    cardTypes = CardTypeEnum;

    international$ = this.store.select(selectShipmentEditorInternational);
    declaration$ = this.store.select(selectShipmentEditorDeclaration);
    quickQuote$ = this.store.select(selectQuickQuote);

    largeMode$ = this.breakpointObserver.observe(['(min-width: 1400px)']).pipe(
        map(result => result.matches)
    );

    fullWidthPackages$: Observable<boolean> = combineLatest([
        this.store.select(selectShipmentEditorDeclaration),
        this.store.select(selectQuickQuote)
    ]).pipe(
        map(([declaration, quickQuote]): boolean => declaration || quickQuote),
        delay(0),
        startWith(true)
    );

    courierOptionDefinitions$ = this.store.select(selectCourierOptions);
    referenceDefinitions$ = this.store.select(selectReferenceDefinitions);

    shipperSummary$: Observable<string> = this.store.select(selectShipmentEditorShipment).pipe(
        map(s => ShipmentSummary.shipperSummary(s))
    );
    shipperError$: Observable<string|null> = this.store.select(selectShipmentEditorShipment).pipe(
        map(s => this.shipmentValidator.validateShipperForQuoting(s))
    );
    destinationSummary$: Observable<string> = this.store.select(selectShipmentEditorShipment).pipe(
        map(s => ShipmentSummary.destinationSummary(s))
    );
    destinationError$: Observable<string|null> = this.store.select(selectShipmentEditorShipment).pipe(
        map(s => this.shipmentValidator.validateDestinationForQuoting(s))
    );
    soldToSummary$: Observable<string> = this.store.select(selectShipmentEditorShipment).pipe(
        map(s => ShipmentSummary.soldToSummary(s))
    );
    soldToError$: Observable<string|null> = this.store.select(selectShipmentEditorShipment).pipe(
        map(s => this.shipmentValidator.validateSoldToForQuoting(s))
    );
    brokerSummary$: Observable<string> = this.store.select(selectShipmentEditorShipment).pipe(
        map(s => ShipmentSummary.brokerSummary(s))
    );
    brokerError$: Observable<string|null> = this.store.select(selectShipmentEditorShipment).pipe(
        map(s => this.shipmentValidator.validateBrokerForQuoting(s))
    );
    packageSummary$: Observable<string> = this.store.select(selectShipmentEditorShipment).pipe(
        map(s => ShipmentSummary.packageSummary(s))
    );
    optionSummary$: Observable<string> = this.store.select(selectShipmentEditorShipment).pipe(
        map(s => ShipmentSummary.optionSummary(s))
    );
    internationalSummary$: Observable<string> = this.store.select(selectShipmentEditorShipment).pipe(
        map(s => ShipmentSummary.internationalSummary(s))
    );
    referencesSummary$: Observable<string> = this.store.select(selectShipmentEditorShipment).pipe(
        map(s => ShipmentSummary.referencesSummary(s))
    );
    shipmentCategory$: Observable<ShipmentCategoryEnum> = this.store.select(selectShipmentEditorCategory);
    brokerDisplay$: Observable<boolean> = combineLatest([ this.shipmentCategory$, this.international$ ]).pipe(
        map(([category, international]) => category === ShipmentCategoryEnum.LTL && international)
    );
    insuranceDisplay$: Observable<boolean> = combineLatest([
        this.store.select(selectShipmentEditorInsuranceProvisionId),
        this.store.select(selectInsuranceCategories),
        this.store.select(selectShipmentEditorHasInsurance)
    ]).pipe(
        map(([ provisionId, categories, hasInsurance ]) => !!provisionId && categories.length > 0 && hasInsurance)
    );
    insuranceSummary$: Observable<string> = combineLatest([
        this.store.select(selectInsuranceCategories),
        this.store.select(selectShipmentEditorInsuranceCategory),
        this.store.select(selectShipmentEditorShipment)
    ]).pipe(
        map(([ categories, selected, shipment ]) => ShipmentSummary.insuranceSummary(shipment, categories.find(c => c.id === selected)))
    );

    private dragEnterCount = 0;
    showDropOverlay = false;

    onDragEnter(event: DragEvent): void {
        if (!event.dataTransfer?.types?.includes('Files')) {
            return;
        }
        this.dragEnterCount++;
        this.showDropOverlay = true;
    }

    onDragLeave(event: DragEvent): void {
        this.dragEnterCount--;
        if (this.dragEnterCount <= 0) {
            this.dragEnterCount = 0;
            this.showDropOverlay = false;
        }
    }

    dragOver(event: DragEvent): void {
        event.preventDefault();
    }

    dropFile(event: DragEvent): void {
        event.preventDefault();
        this.dragEnterCount = 0;
        this.showDropOverlay = false;
        const file = event.dataTransfer?.files?.item(0);
        if (!file) {
            return;
        }
        this.store.dispatch(shipmentEditorOpenDocumentUploadModal({ file }));
    }

    public startQuoting(): void {
        this.store.dispatch(shipmentEditorGetQuotes());
    }

    documents$ = this.store.select(selectShipmentEditorDocuments);

    documentsSummary$: Observable<string> = this.documents$.pipe(
        map(docs => (docs || []).map(d => getDocumentTypeLabel(d.type)).join(', '))
    );

    /*

    activeOptionDefinitionsSubject = new Subject<CourierOptionDefinition[]>();
    // activeOptionDefinitions$: Observable<CourierOptionDefinition[]> = this.activeOptionDefinitionsSubject.asObservable();
    activeFormDefinitions$: Observable<CourierFormDefinition[]>;
    activeReferenceGroupsSubject = new Subject<string[]>();
    activeReferenceGroups$: Observable<string[]> = this.activeReferenceGroupsSubject.asObservable();

    courierOptions: CourierOptionDefinition[];
    referenceGrouping: ReferencesModification;
    courierForms: CourierFormDefinition[];

    referencesGroup = new UntypedFormGroup({});
    courierOptionsGroup = new UntypedFormGroup({});
    courierFormsGroup = new UntypedFormGroup({});
    packagesArray = new UntypedFormArray([]);

    form = new UntypedFormGroup({
        shipper: this.shipperForm,
        destination: this.destinationForm,
        currency: new FormControl(''),
        weightUnit: new FormControl(''),
        packagingType: new FormControl(PackagingTypeEnum.CUSTOMER_PACKAGING),
        documentsOnly: new FormControl(false),
        courierOptions: this.courierOptionsGroup,
        references: this.referencesGroup,
        packages: this.packagesArray,
        shipmentDate: new FormControl(''),
        courierForms: this.courierFormsGroup,
        paymentOption: new FormControl(''),
        paymentAccountNumber: new FormControl(''),
        signatureOption: new FormControl(SignatureOptionEnum.DEFAULT),
    });

    activeOptionDefinitions$ = this.store.select(selectCourierOptions).pipe(
        tap(options => {
            options.forEach(option => {
                if (this.courierFormsGroup.get(option.code) === null) {
                    this.courierFormsGroup.addControl(option.code, new FormControl(''));
                }
                option.parameters.forEach(parameter => {
                    if (this.courierFormsGroup.get(parameter.code) === null) {
                        this.courierFormsGroup.addControl(parameter.code, new FormControl(''));
                    }
                });
            });
        })
    );

    setGroupIds(group: UntypedFormGroup, ids: string[]): void {
        Object.keys(group.controls).forEach((id) => {
            if (ids.indexOf(id) === -1) {
                group.removeControl(id);
            }
        });
        ids.forEach((id) => {
            if (!group.contains(id)) {
                group.addControl(id, new UntypedFormControl(''));
            }
        });
    }

    updatePackagesArray(packages: Package[]): void {
        for (let i = 0; i < packages.length; i++) {
            let group = this.packagesArray.at(i) as UntypedFormGroup;
            const packageEntry = packages[i];
            if (group === undefined) {
                group = new UntypedFormGroup({
                    quantity: new UntypedFormControl(packageEntry.quantity),
                    description: new UntypedFormControl(packageEntry.description),
                    weight: new UntypedFormControl(packageEntry.weight),
                    length: new UntypedFormControl(packageEntry.length),
                    width: new UntypedFormControl(packageEntry.width),
                    height: new UntypedFormControl(packageEntry.height),
                    insuranceValue: new UntypedFormControl(packageEntry.insuranceValue),
                    commodities: new UntypedFormArray([])
                });
                this.packagesArray.push(group);
            }
            const commodityArray = group.get('commodities') as UntypedFormArray;
            for (let j = 0; j < packageEntry.commodities.length; j++) {
                let commodityGroup = commodityArray.at(j);
                const commodityEntry = packageEntry.commodities[j];
                if (commodityGroup === undefined) {
                    commodityGroup = new UntypedFormGroup({
                        description: new UntypedFormControl(commodityEntry.description),
                        harmonizedCode: new UntypedFormControl(commodityEntry.harmonizedCode),
                        unitOfMeasure: new UntypedFormControl(commodityEntry.unitOfMeasure),
                        country: new UntypedFormControl(commodityEntry.country),
                        quantity: new UntypedFormControl(commodityEntry.quantity),
                        price: new UntypedFormControl(commodityEntry.price),
                        weight: new UntypedFormControl(commodityEntry.weight),
                        markings: new UntypedFormControl(commodityEntry.markings)
                    });
                    commodityArray.push(commodityGroup);
                }
            }
            for (let j = commodityArray.controls.length; j >= packageEntry.commodities.length; j--) {
                commodityArray.removeAt(j);
            }
        }
        for (let i = this.packagesArray.controls.length; i >= packages.length; i--) {
            this.packagesArray.removeAt(i);
        }
    }

    setOptionIds(ids: string[]): void {
        this.setGroupIds(this.courierOptionsGroup, ids);
    }

    setReferenceIds(ids: string[]): void {
        this.setGroupIds(this.referencesGroup, ids);
    }

    updateActiveOptionDefinitions(ids: string[]): void {
        const definitions = [];
        if (this.courierOptions) {
            this.courierOptions.forEach((definition) => {
                if (ids.indexOf(definition.code) >= 0) {
                    definitions.push(definition);
                }
            });
        }
        this.activeOptionDefinitionsSubject.next(definitions);
    }

    updateActiveReferenceGroups(ids: string[]): void {
        this.activeReferenceGroupsSubject.next(referenceGroups(ids));
    }

    setupInternationalForms(definitions: CourierFormDefinition[]): void {
        const controls = {};
        if (definitions) {
            definitions.forEach((definition) => {
                controls[definition.code] = new UntypedFormControl('');
                definition.fields.forEach((field) => {
                    controls[field.code] = new UntypedFormControl('');
                });
            });
        }
        this.courierFormsGroup = new UntypedFormGroup(controls);
        this.form.setControl('courierForms', this.courierFormsGroup);
    }

    protected courierOptionCodes(optionDefinitions: CourierOptionDefinition[]): string[] {
        const codes: string[] = [];
        optionDefinitions.forEach(def => {
            codes.push(def.code);
            if (def.parameters) {
                def.parameters.forEach(param => {
                    codes.push(param.code);
                });
            }
        });
        return codes;
    }

    ngAfterViewInit(): void {
        this.shipmentSubscription = this.shipment$.pipe(
            delay(0),
            distinctUntilChanged((x, y) => JSON.stringify(x) === JSON.stringify(y)),
            withLatestFrom(this.store.select(selectShipmentEditorValid)),
            filter(([ , valid ]) => valid),
            map(([ shipment, ]) => shipment),
            withLatestFrom(this.store.select(selectQuickQuote), this.store.select(selectCourierOptions)),
            take(300) // TODO: Remove this as it exists just to stop loops during development
        ).subscribe(([data, quickQuote, courierOptions]) => {
            //this.setOptionIds(this.courierOptionCodes(courierOptions));
            this.setReferenceIds(Object.keys(data.references));
            this.updatePackagesArray(data.packages);
            //this.updateActiveOptionDefinitions(Object.keys(data.courierOptions));
            this.updateActiveReferenceGroups(Object.keys(data.references));
            this.form.patchValue(data, { emitEvent: false });
            this.shipmentValidator.validateForm(this.form, quickQuote);
        });
        this.validitySubscription = this.editorValid$.pipe(delay(0)).subscribe((valid) => {
            if (valid) {
                this.form.enable();
            } else {
                this.form.disable();
            }
        });
    }

    ngOnInit(): void {
        this.store.select(selectCourierOptions).subscribe((courierOptions) => this.courierOptions = courierOptions);
        this.store.select(selectReferenceDefinitions).subscribe((referenceGrouping) => this.referenceGrouping = referenceGrouping);

        this.activeFormDefinitions$ = this.shipment$.pipe(
            withLatestFrom(this.store.select(selectCourierForms)),
            filter(([ , definitions ]) => !!definitions),
            map(([ shipment, definitions ]) =>
                definitions.filter(definition => courierFormFilter(definition, shipment))
            )
        );

        this.store.select(selectCourierForms).subscribe((courierForms) => this.setupInternationalForms(courierForms));

        this.international$ = this.store.select(selectShipmentEditorInternational).pipe(
            delay(0) // emits the value asynchronously (ExpressionChangedAfterItHasBeenCheckedError)
        );
        this.declaration$ = this.store.select(selectShipmentEditorDeclaration).pipe(
            delay(0),
            startWith(false)
        );
        this.quickQuoteSubscription = this.quickQuote$.subscribe((quickQuote) => {
            this.shipmentValidator.validateForm(this.form, quickQuote);
        });

    }

    ngOnDestroy(): void {
        this.shipmentSubscription.unsubscribe();
        this.quickQuoteSubscription.unsubscribe();
        this.validitySubscription.unsubscribe();
    }

    userChange(): void {
        const shipment = this.form.value;
        this.shipmentValidator.validateForm(this.form, false);
        this.store.dispatch(shipmentEditorUpdate({ shipment }));
    }

    save(): void {
        this.store.dispatch(shipmentEditorSaveRequest());
    }


     */
}
